/**
 * Things you need to do. A list of tasks for the current user with
 * one-click resolution.
 *
 * Two kinds of row, told apart by `kind`:
 *   - 'mention': a real comment that @-mentions me. Done = ack.
 *   - 'nudge'  : a synthetic prompt (e.g. write your intent). Done =
 *                actually do the action; we don't store dismissals.
 *
 * Renders in two contexts:
 *   - /             → all my tasks across every board (homepage inbox)
 *   - /boards/:id   → just this board's tasks
 */

import { useEffect, useState } from 'react';
import { api, type TaskRow } from '../api';
import { useRouter } from '../router';
import { Avatar } from './Avatar';
import { IconCheck, IconChevronRight, IconSparkles } from './Icon';
import { LoadingBlock } from './Spinner';
import { RenderNodes } from './CommentRender';
import { UiNode } from '../lib/UiNode';
import { formatDueDate } from '../../shared/comment-body';

export function TasksList({
  boardId,
  emptyHint,
  compact = false,
}: {
  boardId?: string;
  emptyHint?: string;
  compact?: boolean;
}) {
  const [tasks, setTasks] = useState<TaskRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { navigate } = useRouter();

  useEffect(() => {
    let cancelled = false;
    setTasks(null);
    const load = () => {
      void api
        .myTasks(boardId)
        .then((r) => {
          if (!cancelled) setTasks(r.tasks);
        })
        .catch((e) => {
          if (!cancelled) setError(e instanceof Error ? e.message : 'Failed');
        });
    };
    load();
    // Refresh on window focus + on the in-app 'synfolio:tasks-stale'
    // signal so nudges clear the moment you take the underlying action.
    const onFocus = () => load();
    const onStale = () => load();
    window.addEventListener('focus', onFocus);
    window.addEventListener('synfolio:tasks-stale', onStale);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('synfolio:tasks-stale', onStale);
    };
  }, [boardId]);

  async function markMentionDone(t: TaskRow & { kind: 'mention' }) {
    setTasks((cur) => (cur ?? []).filter((x) => keyOf(x) !== keyOf(t)));
    try {
      await api.completeTask(t.commentId);
    } catch {
      const r = await api.myTasks(boardId);
      setTasks(r.tasks);
    }
  }

  if (error) return <p className="text-xs text-rust">{error}</p>;
  if (tasks === null) return <LoadingBlock label="Loading your tasks…" size={20} />;

  if (tasks.length === 0) {
    return (
      <UiNode
        name="inbox-tasks-list"
        entity="tasks"
        state="empty"
        className="rounded-2xl border border-dashed border-ink-rim/60 p-6 text-center text-sm text-cream-3"
      >
        {emptyHint ?? "Nothing to do. You're all caught up."}
      </UiNode>
    );
  }

  return (
    <UiNode
      as="ul"
      name="inbox-tasks-list"
      entity="tasks"
      state="filled"
      className="flex flex-col gap-2"
    >
      {tasks.map((t) => (
        <UiNode
          as="li"
          key={keyOf(t)}
          name="inbox-task-row"
          entity="task"
          entityId={t.kind === 'mention' ? t.commentId : t.nudgeId}
          action="open"
          state={t.kind}
          className="panel-elev flex items-start gap-3 p-3"
        >
          {t.kind === 'mention' ? (
            <Avatar
              user={{
                id: t.authorId,
                name: t.authorName,
                avatarHue: hueFor(t.authorId),
                avatarUrl: null,
              }}
              size={28}
            />
          ) : (
            <span
              aria-hidden
              className="flex h-7 w-7 items-center justify-center rounded-full"
              style={{ background: 'color-mix(in oklab, var(--color-clove) 22%, transparent)' }}
            >
              <IconSparkles size={14} />
            </span>
          )}
          <button
            type="button"
            className="min-w-0 flex-1 text-left"
            onClick={() => navigate(t.href)}
          >
            <div className="mb-0.5 flex items-baseline gap-2 text-xs text-cream-3">
              {t.kind === 'mention' ? (
                <>
                  <span className="font-medium text-cream-2">{t.authorName}</span>
                  <span>on</span>
                  {!boardId && <span className="text-cream-2">{t.boardName} ·</span>}
                  <span className="truncate text-cream-2">{t.subjectLabel}</span>
                </>
              ) : (
                <>
                  {!boardId && <span className="text-cream-2">{t.boardName} ·</span>}
                  <span className="uppercase tracking-wide">For alignment</span>
                </>
              )}
            </div>
            <div className={`break-words text-sm ${compact ? 'line-clamp-2' : ''}`}>
              <RenderNodes nodes={t.bodyNodes} />
            </div>
          </button>
          <div className="flex flex-col items-end gap-1">
            {t.dueIso && (
              <span className="chip" style={dueStyle(t.dueIso)} title={t.dueIso}>
                {formatDueDate(t.dueIso)}
              </span>
            )}
            {t.kind === 'mention' ? (
              <UiNode
                as="button"
                name="inbox-task-done-button"
                entity="task"
                entityId={t.commentId}
                action="mark_done"
                type="button"
                className="btn btn-ghost inline-flex items-center gap-1 text-xs"
                onClick={() => markMentionDone(t)}
                title="Mark this task done"
              >
                <IconCheck size={12} />
                Done
              </UiNode>
            ) : (
              <UiNode
                as="button"
                name="inbox-task-write-button"
                entity="task"
                entityId={t.nudgeId}
                action="take_action"
                type="button"
                className="btn btn-primary inline-flex items-center gap-1 text-xs"
                onClick={() => navigate(t.href)}
                title="Write your intent for this board"
              >
                Write
                <IconChevronRight size={11} />
              </UiNode>
            )}
          </div>
        </UiNode>
      ))}
    </UiNode>
  );
}

export function keyOf(t: TaskRow): string {
  return t.kind === 'mention' ? `m:${t.commentId}` : `n:${t.nudgeId}`;
}

function dueStyle(iso: string): React.CSSProperties {
  const now = new Date();
  const target = new Date(iso + 'T00:00:00');
  const days = Math.round((+target - +new Date(now.toDateString())) / 86_400_000);
  if (days < 0) {
    return {
      background: 'color-mix(in oklab, var(--color-rust) 22%, transparent)',
      color: 'var(--color-rust-2, var(--color-rust))',
      borderColor: 'color-mix(in oklab, var(--color-rust) 40%, transparent)',
    };
  }
  if (days <= 1) {
    return {
      background: 'color-mix(in oklab, var(--color-clove) 22%, transparent)',
      color: 'var(--color-clove-2)',
      borderColor: 'color-mix(in oklab, var(--color-clove) 40%, transparent)',
    };
  }
  return {};
}

function hueFor(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return h % 360;
}
